import Heading from '@/components/Heading';
import Code from '@/components/Code';
import Link from '@/components/Link';
import TimeField from '@/components/TimeField';

<Heading level={2}>Standalone TimeField Example</Heading>

Use the standalone TimeField for client-side interactions that don't involve form submission.

<div className="card bg-base-200 shadow-sm max-w-md">
  <div className="card-body">
    <TimeField
      label={props.label}
      value={props.value}
      onChange={props.onChange}
      isDisabled={props.isDisabled}
      isLoading={props.isLoading}
      description={props.description}
      hourCycle={props.hourCycle}
    />
  </div>
</div>

<details open>
<summary>Source Code</summary>

<Code
  readOnly={true}
  language="tsx"
  value={`import { useState } from 'react';
import TimeField from '@/components/TimeField';

/* ... */

  const [value, setValue] = useState({ hour: 12, minute: 0 });

  const handleChange = (newValue) => {
    setValue(newValue);
  };

/* ... */

    <TimeField
      label="${props.label}"
      value={value}
      isLoading={${props.isLoading ? 'true' : 'false'}}
      isDisabled={${props.isDisabled ? 'true' : 'false'}}
      description="${props.description || ''}"
      hourCycle={${props.hourCycle}}
      onChange={handleChange}
    />
`}
/>
</details>

<Heading level={2}>Standalone TimeField Props</Heading>

<div className="table-container">
  <table className="table table-zebra">
    <thead>
      <tr>
        <th>Prop</th>
        <th>Type</th>
        <th>Required</th>
        <th>Description</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><code>value</code></td>
        <td><code>TimeValue</code> | <code>Date</code> | <code>null</code></td>
        <td>❌</td>
        <td>The current value of the time field</td>
      </tr>
      <tr>
        <td><code>onChange</code></td>
        <td><code>(value: TimeValue | null) =&gt; void</code></td>
        <td>❌</td>
        <td>Handler called when the time value changes</td>
      </tr>
      <tr>
        <td><code>label</code></td>
        <td><code>string</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>The label text displayed above the time field</td>
      </tr>
      <tr>
        <td><code>description</code></td>
        <td><code>string</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Optional description text displayed below the time field</td>
      </tr>
      <tr>
        <td><code>errorMessage</code></td>
        <td><code>string</code> | <code>function</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Error message to display</td>
      </tr>
      <tr>
        <td><code>isLoading</code></td>
        <td><code>boolean</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Shows skeleton loading state when true</td>
      </tr>
      <tr>
        <td><code>isDisabled</code></td>
        <td><code>boolean</code> | <code>undefined</code></td>
        <td>❌</td>
        <td>Disables the field when true</td>
      </tr>
      <tr>
        <td><code>hourCycle</code></td>
        <td><code>12 | 24</code></td>
        <td>❌</td>
        <td>Controls 12/24 hour display (defaults to 24)</td>
      </tr>
    </tbody>
  </table>
</div>

<div className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-400 dark:border-blue-600 mt-8 p-4 text-blue-700 dark:text-blue-300">
  <p className="font-medium">Developer Note:</p>
  <p className="text-sm">This component follows React Aria's TimeField API with our standard UX for loading and errors. See the <Link to="https://react-spectrum.adobe.com/react-aria/TimeField.html#props" external={true}>React Aria TimeField documentation</Link> for details.</p>
</div>


